import { useState, useCallback, useRef, useEffect } from 'react';

export interface PlaceSuggestion {
  id: string;
  place_name: string;
  text: string;
  center: [number, number]; // [lng, lat]
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Load Google Maps script
let googleMapsLoaded = false;
let googleMapsLoading = false;
const loadCallbacks: (() => void)[] = [];

const loadGoogleMapsScript = (): Promise<void> => {
  return new Promise((resolve) => {
    // Check if already fully loaded with Geocoder available
    if (googleMapsLoaded && window.google?.maps?.Geocoder) {
      resolve();
      return;
    }

    // If script is loaded but Geocoder not ready, poll for it
    if (window.google?.maps) {
      const checkReady = setInterval(() => {
        if (window.google?.maps?.Geocoder && window.google?.maps?.places) {
          clearInterval(checkReady);
          googleMapsLoaded = true;
          resolve();
        }
      }, 50);
      return;
    }

    if (googleMapsLoading) {
      loadCallbacks.push(resolve);
      return;
    }

    // Check if script already exists (loaded by another component)
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      googleMapsLoading = true;
      const checkReady = setInterval(() => {
        if (window.google?.maps?.Geocoder && window.google?.maps?.places) {
          clearInterval(checkReady);
          googleMapsLoaded = true;
          googleMapsLoading = false;
          resolve();
          loadCallbacks.forEach(cb => cb());
          loadCallbacks.length = 0;
        }
      }, 50);
      return;
    }

    googleMapsLoading = true;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geocoding&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Poll until the API is fully initialized
      const checkReady = setInterval(() => {
        if (window.google?.maps?.Geocoder && window.google?.maps?.places) {
          clearInterval(checkReady);
          googleMapsLoaded = true;
          googleMapsLoading = false;
          resolve();
          loadCallbacks.forEach(cb => cb());
          loadCallbacks.length = 0;
        }
      }, 50);
    };
    script.onerror = () => {
      googleMapsLoading = false;
      console.error('Failed to load Google Maps script');
    };
    document.head.appendChild(script);
  });
};

export const useGooglePlacesAutocomplete = () => {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  useEffect(() => {
    loadGoogleMapsScript().then(() => {
      if (window.google?.maps?.places) {
        autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
        geocoderRef.current = new google.maps.Geocoder();
      }
    });
  }, []);

  const searchAddress = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);

      try {
        await loadGoogleMapsScript();
        
        if (!autocompleteServiceRef.current) {
          autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
        }

        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: query,
            types: ['establishment', 'geocode'],
          },
          async (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              // Get coordinates for each prediction
              const suggestionsWithCoords: PlaceSuggestion[] = [];
              
              for (const prediction of predictions.slice(0, 5)) {
                try {
                  if (!geocoderRef.current) {
                    geocoderRef.current = new google.maps.Geocoder();
                  }
                  
                  const result = await new Promise<google.maps.GeocoderResult | null>((resolve) => {
                    geocoderRef.current!.geocode(
                      { placeId: prediction.place_id },
                      (results, geocodeStatus) => {
                        if (geocodeStatus === google.maps.GeocoderStatus.OK && results?.[0]) {
                          resolve(results[0]);
                        } else {
                          resolve(null);
                        }
                      }
                    );
                  });

                  if (result) {
                    suggestionsWithCoords.push({
                      id: prediction.place_id,
                      place_name: prediction.description,
                      text: prediction.structured_formatting?.main_text || prediction.description.split(',')[0],
                      center: [
                        result.geometry.location.lng(),
                        result.geometry.location.lat(),
                      ],
                    });
                  }
                } catch (err) {
                  console.error('Geocoding error:', err);
                }
              }
              
              setSuggestions(suggestionsWithCoords);
            } else {
              setSuggestions([]);
            }
            setIsLoading(false);
          }
        );
      } catch (error) {
        console.error('Google Places error:', error);
        setSuggestions([]);
        setIsLoading(false);
      }
    }, 300);
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return { suggestions, isLoading, searchAddress, clearSuggestions };
};

// Geocoding utilities
export const geocodeAddress = async (address: string): Promise<[number, number] | null> => {
  await loadGoogleMapsScript();
  
  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
        resolve([
          results[0].geometry.location.lng(),
          results[0].geometry.location.lat(),
        ]);
      } else {
        resolve(null);
      }
    });
  });
};

export const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
  await loadGoogleMapsScript();
  
  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
        // Find city/locality
        for (const component of results[0].address_components) {
          if (component.types.includes('locality') || component.types.includes('postal_town')) {
            resolve(component.long_name);
            return;
          }
        }
        // Fallback to first result
        resolve(results[0].formatted_address.split(',')[0]);
      } else {
        resolve(null);
      }
    });
  });
};

export const loadGoogleMaps = loadGoogleMapsScript;
